/*
  Session storage helpers.

  Historically these read a cookie named 'login'. Nothing ever set it - the line
  that would have is still commented out in the login saga - so getAccessToken()
  always returned ''. Login actually persists into localStorage or sessionStorage
  under 'login', depending on "remember me".

  Tokens are kept under their OWN key rather than inside that login record, and
  that is the important part. The profile sagas rebuild the login record from
  redux and write it back on every profile fetch and update:

      const newData = [{ ...oldData, ...data.recordset[0] }]
      localStorage.setItem("login", JSON.stringify(newData))

  If the tokens lived in that record, redux's copy - which is whatever login put
  there, not whatever the last refresh rotated to - would be written back over
  the current ones, and the next request would fail with a token that had already
  been replaced. Keeping them separate means those writes cannot touch them.

  Precedence matches the profile reducer: session first, then local.
*/

const LOGIN_KEY = 'login';
const TOKEN_KEY = 'auth_tokens';

/* Storage can throw in private mode or with site data blocked. Never let that
   break rendering - an absent token just means "not logged in". */
const safe = (fn, fallback) => {
    try { return fn(); } catch (e) { return fallback; }
};

const readJSON = (key) => safe(() => {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
}, null);

/* Write back to whichever store already holds the session, so "remember me" is
   preserved without having to be passed in again. */
const storeFor = (key) => safe(
    () => (sessionStorage.getItem(key) !== null ? sessionStorage : localStorage),
    localStorage
);

export default function cookie() {
    const login = readJSON(LOGIN_KEY);
    const record = Array.isArray(login) ? login[0] : login;
    return (record && record.employee_id) || '';
}

export function getAccessToken() {
    const tokens = readJSON(TOKEN_KEY);
    return (tokens && tokens.accessToken) || '';
}

export function getRefreshToken() {
    const tokens = readJSON(TOKEN_KEY);
    return (tokens && tokens.refreshToken) || '';
}

/*
  Called once at login with the pair the server issued. `remember` decides which
  store the session lives in, matching what the login saga does with the profile.
*/
export function saveTokens({ accessToken, refreshToken }, remember) {
    if (!accessToken || !refreshToken) return;

    safe(() => {
        const store = remember ? localStorage : sessionStorage;
        store.setItem(TOKEN_KEY, JSON.stringify({ accessToken, refreshToken }));
    });
}

/*
  Called after every refresh. BOTH values must be saved - the refresh token
  rotates, and keeping the old one locks the session out on the next renew.
*/
export function updateTokens(accessToken, refreshToken) {
    if (!accessToken || !refreshToken) return;

    safe(() => {
        storeFor(TOKEN_KEY).setItem(TOKEN_KEY, JSON.stringify({ accessToken, refreshToken }));
    });
}

/* Retained for the old call signature; a rotation must save both, so prefer
   updateTokens. */
export function updateAccessToken(accessToken) {
    updateTokens(accessToken, getRefreshToken());
}

export function clearTokens() {
    safe(() => {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
    });
}
